import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import config from 'config';
import { useLanguage } from 'hooks/useLanguage';
import { useEffect } from 'react';
import { useAppSelector } from 'store/hooks';
import { GTM } from 'utils/analytics';

export type AppFeatures = {
  can_change_language: boolean;
  // Gates the review details on shop reviews: item box naming who sold or bought, automatic review info (DEV-2612).
  feedback_role_tag_visible: boolean;
  multilang_shop_descriptions_enabled: boolean;
  listing_ai_prefill_enabled: boolean;
  // Picks the invite banner arm (DEV-2320): reward shows the referral banner and page; control has no web banner (app-only bare share).
  buyer_referral_reward_enabled: boolean;
  price_drop_badge: boolean;
  // Fills list-based pickup point providers with the point nearest to the buyer's saved address (DEV-2420).
  checkout_delivery_prefill_from_address: boolean;
  // Ranks the providers the buyer is ready to go with first and selects the top one (DEV-2422).
  checkout_delivery_options_rank_ready_first: boolean;
  // Test arm of the product page delivery line that shows campaign and bundling prices, evaluated
  // only when a campaign lowered a fee on the item or a bundling hour is open (DEV-2352).
  pdp_delivery_campaign_line: boolean;
  // Test arm of the product page delivery line naming the provider checkout lists first instead of the
  // cheapest one, evaluated only when the two differ (DEV-2352). Checkout itself is not affected.
  pdp_delivery_line_checkout_order: boolean;
  // Shows the "My address" section above Delivery at checkout (DEV-2548, web side of DEV-2421).
  checkout_address_ask: boolean;
  // Blocks paying until a buyer who sees the address section has added one (DEV-2548, web side of DEV-2448).
  checkout_address_required: boolean;
  // Test arm showing the provider fee struck through next to a delivery campaign price, evaluated only
  // when a campaign lowered a fee, same flag as the app (DEV-2348, web side in DEV-2611).
  checkout_delivery_campaign_price: boolean;
  // Replaces the listing editor's province and town fields with the seller's profile address (DEV-2429).
  listing_address_line: boolean;
  // Asks sellers to verify their phone with an SMS code before publishing, and shows the number delivery
  // providers call above the publish button (DEV-2547). Needs otp_enabled on in the same country.
  listing_phone_verification: boolean;
};

// How long a flag-gated view waits for GrowthBook before rendering with defaults, same as mobile.
export const FEATURES_READY_TIMEOUT_MS = 2000;

const gb = new GrowthBook<AppFeatures>({
  apiHost: config.get('growthBook.apiHost'),
  clientKey: config.get('growthBook.clientKey'),
  enableDevMode: config.get('growthBook.enableDevMode'),
  // Only required for A/B testing
  // Called every time a user is put into an experiment
  trackingCallback: (experiment, result) => {
    GTM.experimentViewed({ experimentId: experiment.key, variationId: result.key });
  },
});

gb.init().catch((err) => console.error('Failed to initialize GrowthBook', err));

const GrowthBookWrapper = ({ children }) => {
  const { yagaUUID, user } = useAppSelector(({ userData }) => userData);
  const { country } = useLanguage();

  useEffect(() => {
    gb.setAttributes({
      userId: user?.id,
      userCountry: user?.country,
      userAnonymousId: yagaUUID,
      mobile: false,
      platform: 'web',
      isLoggedIn: user?.isLoggedIn,
      siteCountry: country,
    });
  }, [yagaUUID, user?.id, country]);

  return <GrowthBookProvider growthbook={gb}>{children}</GrowthBookProvider>;
};

export default GrowthBookWrapper;
